'use client';

// Updated header component - hydration fix
import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { Search, User, ShoppingCart, X, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CategoryMenuWrapper, MenuItem } from "../category-menu-wrapper";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CartItem, Product } from "@/lib/mock-data";
import { AnimatedPlaceholder } from "../animated-placeholder";
import { HeaderSearchDropdown, type HeaderSearchResult } from "./header-search-dropdown";
import { useAuth } from "@/contexts/auth-context";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type CartItemWithProduct = CartItem & { product: Product };

type CartResponse = {
  items: CartItemWithProduct[];
  totals: {
    subtotal: number;
    tax_total: number;
    total: number;
    shipping_total: number;
    discount_total: number;
  };
};

type SearchResult = HeaderSearchResult;

const SEARCH_HINTS = ["laminates", "louvers", "wall panels", "1 mm walnut"];

interface HeaderProps {
  initialMenuData?: MenuItem[];
}

export function Header({ initialMenuData }: HeaderProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // Prefetch categories on header load - so category pages have cached data
  useEffect(() => {
    queryClient.prefetchQuery({
      queryKey: ["/api/categories"],
      queryFn: async () => {
        const response = await fetch('/api/categories');
        if (!response.ok) throw new Error('Categories not found');
        return response.json();
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  }, [queryClient]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const { customer, logout } = useAuth();

  // Refs for click outside detection and input focus management
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const mobileSearchContainerRef = useRef<HTMLDivElement>(null);
  const desktopSearchInputRef = useRef<HTMLInputElement>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);

  // Sync search query with URL parameter
  useEffect(() => {
    const urlSearchQuery = searchParams?.get('search');
    if (urlSearchQuery !== null && urlSearchQuery !== searchQuery) {
      setSearchQuery(urlSearchQuery);
    } else if (urlSearchQuery === null && searchQuery !== '') {
      // Clear search query if URL doesn't have search parameter
      setSearchQuery('');
    }
  }, [searchParams]);

  // Preload both logo images to prevent flicker
  useEffect(() => {
    const logoImg = new window.Image();
    const iconImg = new window.Image();
    logoImg.src = '/hinch-logo.svg';
    iconImg.src = '/hinch-icon.svg';
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const clickedOutsideDesktop = searchContainerRef.current && !searchContainerRef.current.contains(target);
      const clickedOutsideMobile = mobileSearchContainerRef.current && !mobileSearchContainerRef.current.contains(target);

      // Don't close if clicking on a button or interactive element inside the dropdown
      const isInteractiveElement = (target as HTMLElement).tagName === 'BUTTON' ||
        (target as HTMLElement).closest('button');

      if (clickedOutsideDesktop && clickedOutsideMobile && !isInteractiveElement) {
        setShowDropdown(false);
      }
    }

    if (showDropdown) {
      // Use 'click' instead of 'mousedown' to allow onClick handlers to fire first
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showDropdown]);

  const { data: cartData } = useQuery<CartResponse>({
    queryKey: ["/api/cart"],
    queryFn: async () => {
      const response = await fetch('/api/cart', {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch cart: ${response.status}`);
      }
      return response.json();
    },
    enabled: !!customer,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 30 * 1000,
    retry: (failureCount, error) => {
      if (error?.message?.includes('401')) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  const cartItems = cartData?.items || [];
  const safeCartItems = Array.isArray(cartItems) ? cartItems : [];
  const cartItemCount = safeCartItems.reduce((sum, item) => sum + (item.quantity || 0), 0);

  // Popular search suggestions with category mappings
  const popularSearches = [
    { label: "Kitchen", handle: "kitchen-cabinet-finishes-1-mm-decorative-laminates" },
    { label: "Laminates", handle: "laminates-shop-by-category-decorative-laminates" },
    { label: "Wardrobe", handle: "wardrobe-wardrobe-finishes-1-mm-laminates" },
    { label: "Decorative Wall Panels", handle: "decorative-wall-panels-shop-by-type-charcoal-panels" },
    { label: "Hardware & Accessories", handle: "hardware-shop-by-category-cabinet-hinges" },
    { label: "Countertops", handle: "kitchen-countertops-quartz-countertops" }
  ];

  // Debounced search with AbortController to cancel stale requests
  const searchAbortRef = useRef<AbortController | null>(null);
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);

    // Cancel any pending debounce timer
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    if (value.trim().length > 0) {
      // Show suggestions immediately (no API call needed)
      const filtered = popularSearches.filter(item =>
        item.label.toLowerCase().includes(value.toLowerCase())
      );
      setSearchSuggestions(filtered.slice(0, 3).map(item => item.label));
      setShowDropdown(true);

      // Debounce the API call by 350ms
      searchTimerRef.current = setTimeout(async () => {
        // Abort previous in-flight request
        if (searchAbortRef.current) {
          searchAbortRef.current.abort();
        }
        const controller = new AbortController();
        searchAbortRef.current = controller;

        setIsSearching(true);
        try {
          const response = await fetch(
            `/api/variants?search=${encodeURIComponent(value)}&limit=5`,
            { signal: controller.signal }
          );
          if (response.ok) {
            const data = await response.json();
            const results = data.products?.slice(0, 5).map((product: any) => {
              const variant = product.variants?.[0];
              return {
                id: product.id,
                variantId: variant?.id || product.id,
                title: variant?.metadata?.unique_id || variant?.title || product.title,
                handle: product.handle,
                thumbnail: product.thumbnail || product.images?.[0]?.url,
                price: variant?.prices?.[0]?.amount
              };
            }) || [];
            setSearchResults(results);
          }
        } catch (error: any) {
          if (error?.name !== 'AbortError') {
            console.error('Search error:', error);
          }
        } finally {
          setIsSearching(false);
        }
      }, 350);
    } else {
      // Show popular searches when input is empty
      setSearchSuggestions(popularSearches.slice(0, 6).map(item => item.label));
      setSearchResults([]);
      setShowDropdown(true);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowDropdown(false);
      setIsSearchFocused(false);
      // Blur both search inputs
      desktopSearchInputRef.current?.blur();
      mobileSearchInputRef.current?.blur();
      router.push(`/?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setShowDropdown(false);
    setIsSearchFocused(false);
    // Blur both search inputs
    desktopSearchInputRef.current?.blur();
    mobileSearchInputRef.current?.blur();
    // Find the category handle for this suggestion
    const category = popularSearches.find(item => item.label === suggestion);
    if (category) {
      // Navigate to category page
      router.push(`/${category.handle}`);
    } else {
      // Fallback to search if not found
      router.push(`/?search=${encodeURIComponent(suggestion)}`);
    }
  };

  // Hide header on login page for mobile
  if (pathname === '/login') {
    return (
      <header className="hidden sm:sticky sm:top-0 sm:z-50 sm:bg-white/95 sm:shadow-sm" style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}>
        <div className="border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-3">
            {/* Desktop Layout */}
            <div className="hidden sm:flex items-center justify-between h-16 gap-4">
              {/* Logo */}
              <Link href="/" className="flex items-center flex-shrink-0">
                <img
                  src="/hinch-logo.svg"
                  alt="HINCH"
                  width={120}
                  height={24}
                  className="h-6 w-auto"
                />
              </Link>

              {/* Spacer to push icons to the right */}
              <div className="flex-1"></div>

              {/* Right Icons */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <Link href="/cart" aria-label="Shopping cart">
                  <Button variant="ghost" size="icon" className="relative hover:bg-yellow-50 transition-colors" aria-label="Shopping cart">
                    <ShoppingCart className="w-5 h-5" />
                    {cartItemCount > 0 && (
                      <span className="absolute -top-2 -right-2 bg-orange-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {cartItemCount}
                      </span>
                    )}
                  </Button>
                </Link>
                {customer ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="hover:bg-yellow-50 transition-colors" aria-label="User account menu">
                        <User className="w-5 h-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="w-56 bg-white border border-gray-100 shadow-xl rounded-xl p-2 mt-2"
                      sideOffset={8}
                    >
                      <DropdownMenuLabel className="px-3 py-2 text-sm font-semibold text-gray-900 bg-gray-50 rounded-lg mb-1">
                        {customer.first_name} {customer.last_name}
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator className="my-2 bg-gray-100" />
                      <DropdownMenuItem
                        onClick={() => router.push('/profile')}
                        className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-lg cursor-pointer transition-colors"
                      >
                        Profile
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => router.push('/orders')}
                        className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-lg cursor-pointer transition-colors"
                      >
                        My Orders
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="my-2 bg-gray-100" />
                      <DropdownMenuItem
                        onClick={logout}
                        className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 rounded-lg cursor-pointer transition-colors"
                      >
                        Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link href="/login" aria-label="Login">
                    <Button variant="ghost" size="icon" className="hover:bg-yellow-50 transition-colors" aria-label="Login">
                      <User className="w-5 h-5" />
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
    );
  }

  const hasDropdown = showDropdown && (searchSuggestions.length > 0 || searchResults.length > 0 || isSearching);

  const clearSearch = () => {
    setSearchQuery("");
    setShowDropdown(false);
    setSearchResults([]);
    setSearchSuggestions(popularSearches.slice(0, 6).map(item => item.label));
  };

  const openResult = (result: HeaderSearchResult) => {
    setSearchQuery(result.title);
    setShowDropdown(false);
    setIsSearchFocused(false);
    desktopSearchInputRef.current?.blur();
    mobileSearchInputRef.current?.blur();
    router.push(`/?search=${encodeURIComponent(result.title)}`);
  };

  const dropdown = hasDropdown ? (
    <HeaderSearchDropdown
      suggestions={searchSuggestions}
      results={searchResults}
      isSearching={isSearching}
      onSuggestion={handleSuggestionClick}
      onResult={openResult}
    />
  ) : null;

  // Shared input props for the desktop and mobile search boxes
  const inputProps = (ref: React.RefObject<HTMLInputElement | null>, mobile: boolean) => ({
    ref,
    type: "search" as const,
    value: searchQuery,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value),
    onFocus: () => {
      if (mobile) setIsSearchFocused(true);
      setSearchSuggestions(popularSearches.slice(0, 6).map(item => item.label));
      setShowDropdown(true);
    },
    onBlur: () => {
      if (mobile) setIsSearchFocused(false);
      // Delay closing so clicks on dropdown items fire first
      setTimeout(() => setShowDropdown(false), 200);
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSearch(e as unknown as React.FormEvent);
      }
    },
    autoComplete: "off",
    "aria-label": "Search materials",
    className: `w-full rounded-xl border bg-hv-paper pl-11 pr-11 text-[15px] text-hv-ink outline-none transition-colors placeholder:text-transparent focus:bg-white [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden ${mobile ? 'h-11' : 'h-12'} ${showDropdown ? 'border-hv-orange bg-white' : 'border-hv-line hover:border-[#CFC7B7]'}`,
  });

  const searchBox = (mobile: boolean) => (
    <>
      <Search className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-hv-soft" />
      <input {...inputProps(mobile ? mobileSearchInputRef : desktopSearchInputRef, mobile)} />
      {!searchQuery && (
        <div className="pointer-events-none absolute left-11 right-11 top-1/2 -translate-y-1/2 overflow-hidden whitespace-nowrap text-[15px] text-hv-soft">
          <AnimatedPlaceholder placeholders={SEARCH_HINTS} />
        </div>
      )}
      {(searchQuery || showDropdown) && (
        <button
          type="button"
          onClick={clearSearch}
          className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-hv-soft transition-colors hover:bg-hv-card hover:text-hv-ink"
          aria-label="Clear search"
        >
          <X className="h-4 w-4" />
        </button>
      )}
      {dropdown}
    </>
  );

  const iconBase = "relative flex h-12 w-12 items-center justify-center rounded-xl border text-hv-ink transition-colors hover:border-hv-ink";
  const iconBtn = `${iconBase} border-hv-line bg-white`;

  return (
    <>
      {/* Dim the page on mobile while search suggestions are open */}
      {showDropdown && (
        <div
          className="fixed inset-0 top-16 z-40 bg-black/30 backdrop-blur-sm lg:hidden"
          onClick={() => {
            setShowDropdown(false);
            setIsSearchFocused(false);
            mobileSearchInputRef.current?.blur();
          }}
        />
      )}

      <header className="sticky top-0 z-50 border-b border-hv-line bg-white font-lexend text-hv-ink">
        {/* Desktop */}
        <div className="mx-auto hidden h-[72px] w-full max-w-[1440px] items-center gap-6 px-10 lg:flex xl:px-16">
          <Link href="/" className="flex flex-shrink-0 items-center" aria-label="HINCH home">
            <img src="/hinch-logo.svg" alt="HINCH" width={128} height={28} className="h-7 w-auto" />
          </Link>

          <form onSubmit={handleSearch} className="relative mx-auto flex-1 max-w-[680px]" role="search">
            <div ref={searchContainerRef} className="relative">{searchBox(false)}</div>
          </form>

          <div className="flex flex-shrink-0 items-center gap-2.5">
            {customer ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className={iconBtn} aria-label="Account menu">
                    <User className="h-5 w-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={8} className="w-56 rounded-xl border border-hv-line bg-white p-2 font-lexend shadow-xl">
                  <DropdownMenuLabel className="mb-1 rounded-lg bg-hv-paper px-3 py-2 text-sm font-semibold text-hv-ink">
                    {customer.first_name} {customer.last_name}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="my-2 bg-hv-line" />
                  <DropdownMenuItem onClick={() => router.push('/profile')} className="cursor-pointer rounded-lg px-3 py-2 text-sm text-hv-ink hover:bg-hv-paper">Profile</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => router.push('/wishlist')} className="cursor-pointer rounded-lg px-3 py-2 text-sm text-hv-ink hover:bg-hv-paper">My Wishlist</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => router.push('/orders')} className="cursor-pointer rounded-lg px-3 py-2 text-sm text-hv-ink hover:bg-hv-paper">My Orders</DropdownMenuItem>
                  <DropdownMenuSeparator className="my-2 bg-hv-line" />
                  <DropdownMenuItem onClick={logout} className="cursor-pointer rounded-lg px-3 py-2 text-sm text-hv-red hover:bg-red-50">Logout</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button onClick={() => router.push('/login')} className={iconBtn} aria-label="Sign in">
                <User className="h-5 w-5" />
              </button>
            )}
            <Link href="/wishlist" className={iconBtn} aria-label="Wishlist">
              <Heart className="h-5 w-5" />
            </Link>
            <Link href="/cart" className={`${iconBase} border-transparent bg-hv-yellow`} aria-label={`Cart${cartItemCount ? `, ${cartItemCount} items` : ''}`}>
              <ShoppingCart className="h-5 w-5" />
              {cartItemCount > 0 && (
                <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-md bg-hv-ink px-1 font-hv-mono text-[11px] text-white">
                  {cartItemCount}
                </span>
              )}
            </Link>
          </div>
        </div>

        {/* Mobile */}
        <div className="hv-header-mobile h-16 items-center gap-3 px-4">
          <Link
            href="/"
            aria-label="HINCH home"
            className="flex flex-shrink-0 items-center"
            onClick={(e) => { if (isSearchFocused) e.preventDefault(); }}
          >
            {/* While searching only the wordmark is dropped: the same logo file is clipped to its
                mark (ink spans 0-758 of the 2611-wide artwork) so the symbol stays identical. */}
            <span
              className="block h-[22px] overflow-hidden"
              style={{ width: isSearchFocused ? 30 : 104 }}
            >
              <img src="/hinch-logo.svg" alt="HINCH" width={104} height={22} className="h-[22px] w-auto max-w-none" />
            </span>
          </Link>
          <form onSubmit={handleSearch} className="relative flex-1" role="search">
            <div ref={mobileSearchContainerRef} className="relative">{searchBox(true)}</div>
          </form>
        </div>

        {/* Department menu */}
        <CategoryMenuWrapper onMenuOpen={() => setShowDropdown(false)} initialMenuData={initialMenuData} />
      </header>
    </>
  );
}
